import { FC, useEffect, useMemo, useState } from 'react'
import Button from '@UI/New/Button'
import { crmCallAnalysisApi, CrmCallAnalysisRun } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import classes from './callAnalysis.module.css'
import { callAnalysisStatusLabels as statusLabels } from '@/helpers/callAnalysis'

const criterionStatusLabels = {
	not_applicable: 'Не применимо',
	insufficient_data: 'Недостаточно данных',
}

const warningLabels: Record<string, string> = {
	speaker_roles_inferred: 'Роли участников определены по смыслу',
	speaker_roles_unknown: 'Роли участников определить не удалось',
	evidence_removed: 'Часть неподтверждённых цитат удалена',
	timestamps_unavailable: 'Таймкоды недоступны',
}

const formatTimestamp = (milliseconds: number | null) => {
	if (milliseconds === null) return null
	const seconds = Math.floor(milliseconds / 1000)
	return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

interface Props {
	value: CrmCallAnalysisRun | null
	skipReason?: string | null
}

const skipReasonLabels: Record<string, string> = {
	disabled: 'анализ звонков выключен',
	call_in_progress: 'звонок ещё не завершён',
	migration_missing: 'анализ временно недоступен',
	missed_call: 'звонок пропущен',
	duration_limit: 'длительность звонка превышает 30 минут',
	recording_missing: 'нет записи разговора',
	active_scenario_missing: 'нет настроенных сценариев анализа',
}

const CallAnalysisResult: FC<Props> = ({ value, skipReason }) => {
	const [run, setRun] = useState(value)
	const [retrying, setRetrying] = useState(false)
	const [refreshError, setRefreshError] = useState(false)
	const addAlert = useAddAlert()
	useEffect(() => setRun(value), [value])
	useEffect(() => {
		if (!run || !['pending', 'submitting', 'processing', 'dispatch_error'].includes(run.status)) return
		let cancelled = false
		let timer: ReturnType<typeof setTimeout>
		const refresh = async () => {
			try {
				const response = await crmCallAnalysisApi.getRun(run.id)
				if (response.data?.status !== 'success') throw response
				if (cancelled) return
				setRefreshError(false)
				setRun(response.data.data)
			} catch {
				if (!cancelled) setRefreshError(true)
			}
			if (!cancelled) timer = setTimeout(refresh, 5000)
		}
		timer = setTimeout(refresh, 5000)
		return () => { cancelled = true; clearTimeout(timer) }
	}, [run?.id, run?.status])
	const criteriaNames = useMemo(
		() =>
			Object.fromEntries(
				(run?.criteria ?? []).map((criterion) => [criterion.id, criterion.name])
			),
		[run?.criteria]
	)

	if (!run) return skipReason && skipReasonLabels[skipReason]
		? <div className={classes.meta}>Анализ звонка не запускался: {skipReasonLabels[skipReason]}.</div>
		: null

	const retry = async () => {
		setRetrying(true)
		try {
			const response = await crmCallAnalysisApi.retry(run.id)
			if (response.data?.status !== 'success') throw response
			setRun(response.data.data)
		} catch (error: any) {
			addAlert({
				title: 'Ошибка',
				text:
					error?.response?.data?.error?.[0] ??
					'Не удалось повторно запустить анализ',
				type: 'error',
			})
		} finally {
			setRetrying(false)
		}
	}

	return (
		<div className={classes.wrapper}>
			<div className={classes.header}>
				<strong>Анализ звонка</strong>
				<span className={classes.status}>{statusLabels[run.status]}</span>
			</div>
			{run.scenario_name ? (
				<div className={classes.meta}>
					{run.scenario_name} · версия {run.scenario_version}
				</div>
			) : null}

				{run.analysis && Array.isArray(run.analysis.criteria) ? (
				<>
					<p className={classes.comment}>{run.analysis.overall_comment}</p>
					<div className={classes.criteria}>
						{run.analysis.criteria.map((criterion) => (
							<div className={classes.criterion} key={criterion.criterion_id}>
								<div className={classes.criterionTitle}>
									<span>
										{criteriaNames[criterion.criterion_id] ??
											criterion.criterion_id}
									</span>
									<strong>
										{criterion.status === 'scored'
											? `${criterion.score}/10`
											: criterionStatusLabels[criterion.status]}
									</strong>
								</div>
								{criterion.evidence?.length ? (
									<ul className={classes.evidence}>
										{criterion.evidence.map((evidence, index) => {
											const start = formatTimestamp(evidence.start_ms)
											const end = formatTimestamp(evidence.end_ms)
											return (
												<li key={`${criterion.criterion_id}-${index}`}>
													{start && end ? (
														<span>
															{start}–{end}:{' '}
														</span>
													) : null}
													«{evidence.quote}»
												</li>
											)
										})}
									</ul>
								) : null}
							</div>
						))}
					</div>
				</>
			) : null}

			{run.warnings.length ? (
				<div className={classes.warning}>
					{run.warnings
						.map((warning) => warningLabels[warning] ?? warning)
						.join('. ')}
				</div>
			) : null}

				{run.error && Object.keys(run.error).length > 0 ? (
				<div className={classes.error}>
					{run.error.message ?? 'Не удалось выполнить анализ звонка'}
				</div>
			) : null}
				{refreshError ? <div className={classes.error}>Не удалось обновить статус анализа. Повторим автоматически.</div> : null}

			{run.transcript?.text ? (
				<details className={classes.transcript}>
					<summary>Транскрипт звонка</summary>
					<p>{run.transcript.text}</p>
				</details>
			) : null}

			{run.can_retry ? (
				<Button
					className={classes.retry}
					variant="outline"
					isLoading={retrying}
					disabled={retrying}
					onClick={retry}
				>
					Запустить повторно
				</Button>
			) : null}
		</div>
	)
}

export default CallAnalysisResult
